import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import UpgradeModal from '../components/UpgradeModal';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { useTheme } from '../context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Users, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CofounderDashboard() {
  const { isDark } = useTheme();
  const [profile, setProfile] = useState(null);
  const [cofounders, setCofounders] = useState([]);
  const { upgradeModal, checkFeature, closeUpgradeModal } = useFeatureAccess();

  // Theme classes
  const bg = isDark ? 'bg-slate-900' : 'bg-slate-100';
  const cardBg = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const innerCardBg = isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200';
  const textPrimary = isDark ? 'text-white' : 'text-blue-900';
  const textSecondary = isDark ? 'text-slate-300' : 'text-blue-700';
  const textMuted = isDark ? 'text-slate-400' : 'text-slate-600';
  const badgeBg = isDark ? 'bg-blue-900/60 text-blue-200 border border-blue-700/50' : 'bg-blue-100 text-blue-800';
  const badgeBgAlt = isDark ? 'bg-slate-600 text-slate-100 border border-slate-500' : 'bg-slate-200 text-slate-700';
  const btnSecondary = isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : 'border-slate-300 text-slate-700 hover:bg-slate-100';

  const fetchData = async () => {
    try {
      const [profileRes, cofsRes] = await Promise.all([
        axios.get(`${API}/profile/my-profile`),
        axios.get(`${API}/discovery/recommendations`)
      ]);

      if (profileRes.data.exists) {
        setProfile(profileRes.data.profile);
      }
      setCofounders(cofsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className={`min-h-screen ${bg}`} data-testid="cofounder-dashboard">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <h1 className={`text-3xl sm:text-4xl font-bold mb-2 ${textPrimary}`}>Co-founder Dashboard</h1>
          <p className={textSecondary}>Find your perfect co-founder match.</p>
        </div>

        {!profile ? (
          <Card className={`shadow-sm p-8 mb-8 ${cardBg}`}>
            <div className="text-center">
              <h2 className={`text-2xl font-bold mb-4 ${textPrimary}`}>Complete Your Profile</h2>
              <p className={`mb-6 ${textSecondary}`}>Set up your co-founder profile to start connecting</p>
              <Link to="/profile-setup">
                <Button className="btn-primary" data-testid="setup-profile-btn">Set Up Profile</Button>
              </Link>
            </div>
          </Card>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <Card className={`shadow-sm p-6 ${cardBg}`}>
                <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>My Skills</h2>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map(skill => (
                    <span key={skill} className={`text-sm px-3 py-2 rounded font-medium ${badgeBg}`}>
                      {skill}
                    </span>
                  ))}
                </div>
              </Card>

              <Card className={`shadow-sm p-6 ${cardBg}`}>
                <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>Looking For</h2>
                <div className="flex flex-wrap gap-2">
                  {profile.looking_for.map(skill => (
                    <span key={skill} className={`text-sm px-3 py-2 rounded border ${isDark ? 'bg-slate-700 text-slate-300 border-slate-600' : 'bg-slate-100 text-blue-800 border-slate-200'}`}>
                      {skill}
                    </span>
                  ))}
                </div>
              </Card>
            </div>

            <Card className={`shadow-sm p-6 ${cardBg}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
                <h2 className={`text-xl font-bold ${textPrimary}`}>Discover Co-founders</h2>
                <Link to="/discovery">
                  <Button className={`border ${btnSecondary}`} variant="outline" data-testid="search-cofounders">
                    <Search size={18} className="mr-2" />
                    Search All
                  </Button>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {cofounders.map(cf => (
                  <Card key={cf.user_id} className={`p-4 hover:shadow-md transition-all border ${innerCardBg} ${isDark ? 'hover:border-blue-400' : 'hover:border-blue-500'}`}>
                    <h3 className={`font-semibold mb-2 ${textPrimary}`}>{cf.name}</h3>
                    <p className={`text-xs mb-3 ${textMuted}`}>{cf.profile.city}, {cf.profile.country}</p>
                    <div className="space-y-2">
                      <div>
                        <span className={`text-xs font-medium ${textSecondary}`}>Skills:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {cf.profile.skills.slice(0, 3).map(skill => (
                            <span key={skill} className={`text-xs px-2 py-1 rounded ${badgeBg}`}>
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span className={`text-xs font-medium ${textSecondary}`}>Looking for:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {cf.profile.looking_for.slice(0, 2).map(skill => (
                            <span key={skill} className={`text-xs px-2 py-1 rounded ${badgeBgAlt}`}>
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>

      <UpgradeModal
        isOpen={upgradeModal.isOpen}
        onClose={closeUpgradeModal}
        feature={upgradeModal.feature}
        currentPlan={upgradeModal.currentPlan}
        availablePlans={upgradeModal.availablePlans}
        role="cofounder"
      />
    </div>
  );
}
